import { ArrowLeft, ShoppingCart, Truck, Package, Star, Heart, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import LocationPreview from "@/components/LocationPreview";
import MessageModal from "@/components/MessageModal";

const ProductDetail = () => {
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const navigate = useNavigate();

  // Mock product data - will be replaced with real data from props/API
  const product = {
    id: "1",
    title: "Handgemaakte Biologische Zeep Collectie",
    price: 24.99,
    originalPrice: 32.99,
    description: "Een luxueuze collectie van handgemaakte biologische zepen, vervaardigd met natuurlijke ingrediënten uit België. Elke zeep is met liefde gemaakt door lokale ambachtslieden. Perfect voor de bewuste vrouw die kiest voor duurzame schoonheidsproducten.",
    images: [
      "https://images.pexels.com/photos/4465124/pexels-photo-4465124.jpeg?auto=compress&cs=tinysrgb&w=800",
      "https://images.pexels.com/photos/4465831/pexels-photo-4465831.jpeg?auto=compress&cs=tinysrgb&w=800"
    ],
    stock: 8,
    rating: 4.9,
    reviewCount: 23,
    shipping: {
      freeShipping: true,
      estimatedDays: "2-3 werkdagen",
      locations: "België & Nederland",
      pickupAvailable: true,
      pickupLocation: "Gent, België"
    },
    seller: {
      name: "Sophie's Naturals",
      avatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=200",
      verified: true,
      rating: 4.9,
      location: "Gent, België",
      address: "Korenlei 15, 9000 Gent, België"
    },
    features: [
      "100% biologische ingrediënten",
      "Handgemaakt in België",
      "Milieuvriendelijke verpakking",
      "4 verschillende geuren",
      "Geschikt voor alle huidtypes",
      "Cruelty-free & vegan"
    ],
    deliveryOptions: [
      {
        type: "shipping",
        title: "Verzending",
        description: "Gratis verzending binnen België",
        price: 0,
        timeframe: "2-3 werkdagen"
      },
      {
        type: "pickup",
        title: "Ophalen",
        description: "Ophalen in Gent centrum",
        price: 0,
        timeframe: "Vandaag beschikbaar",
        location: "Gent, België"
      }
    ]
  };

  const handleBuyNow = async () => {
    console.log("Initiating Stripe payment for:", product.title, "Quantity:", quantity);
    // TODO: Integrate with Stripe
    // This will redirect to Stripe checkout or open payment modal
    toast.success("Stripe payment integration zou hier worden geactiveerd!");
  };

  const handleSendMessage = () => {
    console.log("Opening message modal for product inquiry");
    setShowMessageModal(true);
  };

  const handleGoBack = () => {
    console.log("Going back to previous screen");
    navigate(-1);
  };

  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= product.stock) {
      setQuantity(newQuantity);
    }
  };

  const totalPrice = (product.price * quantity).toFixed(2);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f6f1eb' }}>
      {/* Header with back button */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm" style={{ borderBottom: '1px solid #efe7dd' }}>
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleGoBack}
              className="flex items-center gap-2 px-4 py-2 font-medium hover:shadow-md transition-all"
              style={{ 
                backgroundColor: '#fbf8f4',
                borderColor: '#efe7dd',
                color: '#837069'
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Terug</span>
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-semibold" style={{ color: '#837069' }}>
                Product Details
              </h1>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Ontdek unieke producten van woman on a mission
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsWishlisted(!isWishlisted)}
              className="p-2 hover:shadow-md transition-all"
              style={{ 
                backgroundColor: '#fbf8f4',
                borderColor: '#efe7dd',
                color: isWishlisted ? '#ef4444' : '#837069'
              }}
            >
              <Heart className={`h-5 w-5 ${isWishlisted ? 'fill-red-500 text-red-500' : ''}`} />
            </Button>
          </div>

          {/* Product Header */}
          <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: '#efe7dd', border: '1px solid #e6dbd0' }}>
            <div className="text-center">
              <div className="mb-2">
                <h1 className="text-2xl font-light tracking-wider" style={{ color: '#837069' }}>
                  <span className="font-bold" style={{ color: '#a0897c' }}>Product Details</span>
                </h1>
              </div>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Handgemaakt met liefde door vrouwen in België
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 pb-32">
        {/* Product Image */}
        <div className="relative mb-6 rounded-2xl overflow-hidden shadow-lg">
          <img 
            src={product.images[0]} 
            alt={product.title}
            className="w-full h-64 md:h-80 object-cover"
          />
          {product.originalPrice > product.price && (
            <Badge className="absolute top-4 left-4 bg-red-500 text-white">
              Bespaar €{(product.originalPrice - product.price).toFixed(2)}
            </Badge>
          )}
        </div>

        {/* Product Info Card */}
        <Card className="mb-6 border-0 shadow-lg bg-white/80 backdrop-blur-sm" style={{ borderColor: '#efe7dd' }}>
          <CardContent className="p-6">
            {/* Seller Info */}
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-10 w-10">
                <AvatarImage src={product.seller.avatar} alt={product.seller.name} />
                <AvatarFallback style={{ backgroundColor: '#efe7dd', color: '#a0897c' }}>
                  {product.seller.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm" style={{ color: '#837069' }}>{product.seller.name}</h3>
                  {product.seller.verified && (
                    <Badge variant="secondary" className="text-xs" style={{ 
                      backgroundColor: '#efe7dd', 
                      color: '#a0897c' 
                    }}>
                      Verified
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs" style={{ color: '#b6a092' }}>{product.seller.rating} verkoper rating</span>
                </div>
              </div>
            </div>

            {/* Product Title */}
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#837069' }}>
              {product.title}
            </h2>

            {/* Rating and Reviews */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-medium" style={{ color: '#a0897c' }}>{product.rating}</span>
              </div>
              <span className="text-sm" style={{ color: '#b6a092' }}>({product.reviewCount} reviews)</span>
            </div>

            {/* Price Section */}
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-bold" style={{ color: '#837069' }}>€{product.price}</span>
                {product.originalPrice > product.price && (
                  <span className="text-lg line-through" style={{ color: '#b6a092' }}>€{product.originalPrice}</span>
                )}
              </div>
            </div>

            {/* Stock Check */}
            <div className="mb-6">
              {product.stock > 0 ? (
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">
                    {product.stock} op voorraad
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-700">Uitverkocht</span>
                </div>
              )}
            </div>

            <Separator className="my-6" style={{ backgroundColor: '#efe7dd' }} />

            {/* Description */}
            <div className="mb-6">
              <h4 className="font-semibold mb-3" style={{ color: '#837069' }}>Beschrijving</h4>
              <p className="leading-relaxed" style={{ color: '#a0897c' }}>{product.description}</p>
            </div>

            {/* Features */}
            <div className="mb-6">
              <h4 className="font-semibold mb-3" style={{ color: '#837069' }}>Wat zit erbij:</h4>
              <div className="space-y-2">
                {product.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#b6a092' }} />
                    <span className="text-sm" style={{ color: '#a0897c' }}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Information */}
            <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: '#efe7dd' }}>
              <div className="flex items-center gap-2 mb-3">
                <Truck className="h-4 w-4" style={{ color: '#b6a092' }} />
                <span className="text-sm font-medium" style={{ color: '#b6a092' }}>Bezorging & Ophalen</span>
              </div>
              
              {/* Delivery Options */}
              <div className="space-y-3 mb-4">
                {product.deliveryOptions.map((option, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-white/50">
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#837069' }}>
                        {option.title}
                      </p>
                      <p className="text-xs" style={{ color: '#b6a092' }}>{option.description}</p>
                      <p className="text-xs font-medium" style={{ color: '#a0897c' }}>{option.timeframe}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold" style={{ color: '#837069' }}>
                        {option.price === 0 ? 'Gratis' : `€${option.price}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-xs" style={{ color: '#b6a092' }}>
                {product.shipping.locations}
              </div>
            </div>

            {/* Location Preview for Pickup Option */}
            {product.shipping.pickupAvailable && (
              <div className="mb-6">
                <h4 className="font-semibold mb-3" style={{ color: '#837069' }}>Ophaallocatie</h4>
                <LocationPreview
                  location={product.seller.location}
                  address={product.seller.address}
                  serviceType="in-person"
                  compact={false}
                />
              </div>
            )}

            {/* Woman on a Mission Badge */}
            <div className="rounded-lg p-3 mb-6" style={{ backgroundColor: '#fbf8f4', border: '1px solid #efe7dd' }}>
              <p className="font-medium text-sm flex items-center gap-2" style={{ color: '#837069' }}>
                <div className="px-2 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: '#a0897c' }}>
                  WOM
                </div>
                Woman on a Mission - Bewust Product
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fixed Bottom Purchase Section */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t p-6" style={{ borderColor: '#efe7dd' }}>
        <div className="max-w-md mx-auto">
          {/* Quantity Selector */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium" style={{ color: '#837069' }}>Aantal:</span>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
                className="h-8 w-8 p-0"
                style={{ borderColor: '#e6dbd0', color: '#b6a092' }}
              >
                -
              </Button>
              <span className="font-medium min-w-[2ch] text-center" style={{ color: '#837069' }}>{quantity}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= product.stock}
                className="h-8 w-8 p-0"
                style={{ borderColor: '#e6dbd0', color: '#b6a092' }}
              >
                +
              </Button>
            </div>
          </div>

          {/* Total Price */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-semibold" style={{ color: '#837069' }}>Totaal:</span>
            <span className="text-2xl font-bold" style={{ color: '#837069' }}>€{totalPrice}</span>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleSendMessage}
              variant="outline"
              className="flex-1 py-4 text-lg font-semibold rounded-xl transition-all"
              style={{ borderColor: '#e6dbd0', color: '#b6a092' }}
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Vraag Stellen
            </Button>
            <Button 
              onClick={handleBuyNow}
              disabled={product.stock === 0}
              className="flex-1 text-white py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              style={{ backgroundColor: '#b6a092' }}
              size="lg"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              {product.stock > 0 ? 'Koop Nu' : 'Uitverkocht'}
            </Button>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      <MessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        recipientName={product.seller.name}
        recipientAvatar={product.seller.avatar}
        context={{
          type: 'product',
          title: product.title,
          price: product.price,
          image: product.images[0]
        }}
      />
    </div>
  );
};

export default ProductDetail;